import type { AircraftSpec } from "./types";

/** Selectable — not only a leftover. Default certificate is 704. */
export const DUTY_CERT_703_LABEL = "703 Air Taxi";
export const DUTY_CERT_703_LABEL_LEGACY = "703 Air Taxi (sim)";

/** Default company certificate when no FTL scheme is stored. */
export const DUTY_CERT_704_LABEL = "704 Commuter";
export const DUTY_CERT_705_LABEL = "705 Airline";

export type CarCertificateKind = "703" | "704" | "705";

export const CAR_CERTIFICATE_KINDS: readonly CarCertificateKind[] = ["703", "704", "705"];

export const CAR_OPERATOR_OPTIONS: ReadonlyArray<{
  kind: CarCertificateKind;
  label: string;
  hint: string;
}> = [
  { kind: "703", label: DUTY_CERT_703_LABEL, hint: "Air taxi. Real CARs exclude all turbo-jets." },
  { kind: "704", label: DUTY_CERT_704_LABEL, hint: "Default commuter tables — Hawker + PC-12." },
  { kind: "705", label: DUTY_CERT_705_LABEL, hint: "Airline-shaped training preset." },
];

const JET_ICAO_TYPES = new Set(["H25B", "FA50"]);

/** Exact old Celtic default labels — remap to 704 unless the dispatcher picked 703. */
const LEGACY_CELTIC_703_LABELS = new Set([DUTY_CERT_703_LABEL, DUTY_CERT_703_LABEL_LEGACY]);

export function isTurboJetIcaoType(icaoType: string | undefined): boolean {
  return JET_ICAO_TYPES.has((icaoType ?? "").trim().toUpperCase());
}

export function isCarCertificateKind(value: unknown): value is CarCertificateKind {
  return value === "703" || value === "704" || value === "705";
}

/**
 * Default company mapping is 704 (Celtic Air). Per-type helpers no longer
 * split 703 turboprop / 704 jet — the Settings picker owns the live kind.
 */
export function carKindForIcaoType(_icaoType?: string, _cruiseKt?: number): CarCertificateKind {
  return "704";
}

export function carKindForSpec(_spec: Pick<AircraftSpec, "icaoType" | "cruiseKt">): CarCertificateKind {
  return "704";
}

export function carLabelForKind(kind: CarCertificateKind): string {
  if (kind === "705") return DUTY_CERT_705_LABEL;
  if (kind === "703") return DUTY_CERT_703_LABEL;
  return DUTY_CERT_704_LABEL;
}

export function carLabelForSpec(_spec: Pick<AircraftSpec, "icaoType" | "cruiseKt">): string {
  return DUTY_CERT_704_LABEL;
}

export function companyCarKind(_operatedIcaoTypes?: readonly string[]): CarCertificateKind {
  return "704";
}

export function companyCarCaption(_operatedIcaoTypes?: readonly string[]): string {
  return DUTY_CERT_704_LABEL;
}

/** Exact leftover default strings only — not “contains 703” / not every Air Taxi phrase. */
export function isExactLegacyCeltic703Label(label: string | undefined): boolean {
  return LEGACY_CELTIC_703_LABELS.has((label ?? "").trim());
}

export function isUnsetCarCertificate(input: {
  certificateKind?: string;
  certificateLabel?: string;
}): boolean {
  const kind = isCarCertificateKind(input.certificateKind) ? input.certificateKind : undefined;
  const label = input.certificateLabel?.trim() ?? "";
  return !kind && (!label || LEGACY_CELTIC_703_LABELS.has(label));
}

export function isLegacy703Label(label: string | undefined): boolean {
  return isExactLegacyCeltic703Label(label);
}

/** @deprecated Use isExactLegacyCeltic703Label */
export function isLegacyDefault703Label(label: string | undefined): boolean {
  return isExactLegacyCeltic703Label(label);
}

/**
 * Celtic default for new / empty / unset = 704.
 * One-time leftover migrate: no kind + empty or exact old "703 Air Taxi" /
 * "703 Air Taxi (sim)" labels → 704.
 * An explicit certificateKind (703 / 704 / 705) is kept on every hydrate so
 * the Settings picker is not overwritten.
 */
export function migrateCompanyCarCertificate(input: {
  certificateKind?: string;
  certificateLabel?: string;
  certificateUserSet?: boolean;
}): { certificateKind: CarCertificateKind; certificateLabel: string; certificateUserSet: boolean } {
  const label = input.certificateLabel?.trim() ?? "";
  const kind = isCarCertificateKind(input.certificateKind) ? input.certificateKind : undefined;
  const userSet = Boolean(input.certificateUserSet);

  if (kind) {
    const stock = carLabelForKind(kind);
    const nextLabel =
      kind !== "703" && isExactLegacyCeltic703Label(label) ? stock : label || stock;
    return { certificateKind: kind, certificateLabel: nextLabel, certificateUserSet: userSet };
  }

  if (!label || isExactLegacyCeltic703Label(label)) {
    return { certificateKind: "704", certificateLabel: DUTY_CERT_704_LABEL, certificateUserSet: false };
  }

  return { certificateKind: "704", certificateLabel: label, certificateUserSet: false };
}

export const CAR_HAWKER_704_RULE =
  "New careers default to CAR 704 Commuter tables (Hawker 800XP and PC-12). A Hawker / H25B turbo-jet cannot operate under CAR 703 Air Taxi (703 excludes all turbo-jets). Settings → Company lets the dispatcher pick CAR 704 / FAR 135 / EASA and 703 / 704 / 705 — training labels, not a Transport Canada or FAA approval.";
